/**
 * The per-game team-name pool, as a dropdown in the tab header.
 *
 * Replaces the old always-visible panel at the foot of the Teams tab, which
 * rendered all 40 core names as bare checkboxes in an unconstrained wrapping row.
 * Because `_form.scss` styles every `input[type=checkbox]` at 30×30px globally,
 * that list dominated the page and pushed the actual teams — the thing an admin
 * came to look at — below the fold.
 *
 * The names are toggle ROWS in a fixed grid rather than checkboxes, which sidesteps
 * that global rule without changing it (the rest of the app relies on it), and
 * gives each name a full-width hit target and room for an "in use" note.
 *
 * Edits are local until Save, matching the original: an admin unchecking six names
 * should not fire six round-trips, and a half-finished edit should be abandonable
 * by closing the menu.
 */

import { useState, useEffect, useCallback } from 'react';
import { TRL_CORE_TEAM_NAMES } from '@swtorbit/shared-socket-events';
import { useDropdown } from '../../../hooks/useDropdown';

interface TeamNameMenuProps {
  disabledCoreNames: string[];
  customNames: string[];
  /** Names currently taken by a team — these can't be disabled out from under it. */
  usedNames: Set<string>;
  onSave: (disabledCoreNames: string[], customNames: string[]) => void;
}

export function TeamNameMenu({ disabledCoreNames, customNames, usedNames, onSave }: TeamNameMenuProps) {
  const { isOpen, toggle, close, ref } = useDropdown();
  const [disabled, setDisabled] = useState<string[]>(disabledCoreNames);
  const [custom, setCustom] = useState<string[]>(customNames);
  const [newName, setNewName] = useState('');

  // Re-seed local state when the persisted config changes.
  useEffect(() => {
    setDisabled(disabledCoreNames);
  }, [disabledCoreNames]);
  useEffect(() => {
    setCustom(customNames);
  }, [customNames]);

  const toggleCore = useCallback((name: string) => {
    setDisabled((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  }, []);

  const addCustom = useCallback(() => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCustom((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    setNewName('');
  }, [newName]);

  const removeCustom = useCallback((name: string) => {
    setCustom((prev) => prev.filter((n) => n !== name));
  }, []);

  const enabledCount = TRL_CORE_TEAM_NAMES.length - disabled.length + custom.length;

  const handleSave = () => {
    onSave(disabled, custom);
    close();
  };

  return (
    <div className={`dropdown dropdown--end${isOpen ? ' is-open' : ''}`} ref={ref}>
      <button
        type="button"
        className="btn btn--outline btn--slate btn--sm"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={toggle}
        title="Choose which names teams can be given in this game"
      >
        ⚙ Team names <span className="badge badge--sm">{enabledCount}</span>
      </button>

      {isOpen && (
        <div className="dropdown__menu trl-teams__name-menu">
          <div className="trl-teams__name-menu-header">
            <h4 className="m-0">Team name pool</h4>
            <p className="field__hint fs-xs m-0">
              Turn a name off to keep it out of this game, or add your own.
            </p>
          </div>

          <div className="trl-teams__name-grid">
            {TRL_CORE_TEAM_NAMES.map((name) => {
              const isOff = disabled.includes(name);
              const inUse = usedNames.has(name);
              return (
                <button
                  key={name}
                  type="button"
                  className={`trl-teams__name-toggle${isOff ? ' is-off' : ''}`}
                  // A name a team already carries can't be withdrawn — the team
                  // would be left holding a name that is no longer in the pool.
                  disabled={inUse}
                  aria-pressed={!isOff}
                  title={inUse ? `In use by a team — rename that team first` : name}
                  onClick={() => toggleCore(name)}
                >
                  <span className="trl-teams__name-tick" aria-hidden="true">
                    {isOff ? '' : '✓'}
                  </span>
                  <span className="trl-teams__name-label">{name}</span>
                  {inUse && <span className="badge badge--sm">in use</span>}
                </button>
              );
            })}
          </div>

          <div className="trl-teams__name-menu-footer">
            <span className="fw-bold fs-sm">Custom names</span>
            {custom.length === 0 ? (
              <span className="fs-sm text-slate">None yet.</span>
            ) : (
              <div className="d-flex flex-wrap gap-2xs">
                {custom.map((name) => (
                  <span key={name} className="badge d-flex align-items-center gap-2xs">
                    {name}
                    <button
                      className="btn btn--outline btn--slate btn--sm"
                      title="Remove custom name"
                      disabled={usedNames.has(name)}
                      onClick={() => removeCustom(name)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="d-flex gap-2xs align-items-center flex-wrap">
              <input
                type="text"
                className="input"
                placeholder="Add a custom name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addCustom();
                }}
                style={{ flex: 1, minWidth: '140px' }}
              />
              <button className="btn btn--outline btn--slate btn--sm" onClick={addCustom} disabled={!newName.trim()}>
                ➕ Add
              </button>
            </div>

            <div className="d-flex gap-xs justify-content-between align-items-center">
              <button className="btn btn--outline btn--slate btn--sm" onClick={close}>
                Cancel
              </button>
              <button className="btn btn--sm" onClick={handleSave}>
                💾 Save names
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamNameMenu;
